
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Button, Spinner, Alert, ProgressBar, Form, Badge } from 'react-bootstrap';
import api from '../services/api';

const QuizView = () => {
    const { courseId, lessonId } = useParams();
    const navigate = useNavigate();
    const [quiz, setQuiz] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [answers, setAnswers] = useState({});
    const [textAnswers, setTextAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [attempted, setAttempted] = useState(false);

    useEffect(() => {
        fetchQuiz();
    }, [lessonId]);

    const fetchQuiz = async () => {
        try {
            const response = await api.get(`/lessons/${lessonId}/quiz/`);
            setQuiz(response.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Nie udało się pobrać quizu');
        } finally {
            setLoading(false);
        }
    };

    const handleAnswerChange = (questionId, answerId) => {
        setAnswers(prev => {
            const current = prev[questionId] || [];
            if (current.includes(answerId)) {
                return { ...prev, [questionId]: current.filter(id => id !== answerId) };
            } else {
                return { ...prev, [questionId]: [...current, answerId] };
            }
        });
    };

    const handleTextAnswerChange = (questionId, value) => {
        setTextAnswers(prev => ({ ...prev, [questionId]: value }));
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        setError('');

        try {
            const data = {
                answers: answers,
                text_answers: textAnswers
            };
            const response = await api.post(`/lessons/${lessonId}/submit_quiz/`, data);
            setResult(response.data);
            setAttempted(true);
        } catch (err) {
            setError(err.response?.data?.detail || 'Błąd podczas zapisu quizu');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Ładowanie quizu...</p>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-5">
                <Alert variant="danger">{error}</Alert>
                <Button variant="link" onClick={() => navigate(`/courses/${courseId}/lessons/${lessonId}`)}>
                    ← Wróć do lekcji
                </Button>
            </Container>
        );
    }

    if (!quiz) {
        return (
            <Container className="mt-5">
                <Alert variant="warning">Brak quizu dla tej lekcji</Alert>
                <Button variant="link" onClick={() => navigate(`/courses/${courseId}/lessons/${lessonId}`)}>
                    ← Wróć do lekcji
                </Button>
            </Container>
        );
    }

    if (result) {
        const passed = result.percentage >= quiz.passing_score;
        return (
            <Container className="mt-4">
                <Card className="shadow-sm">
                    <Card.Body className="text-center">
                        <h2 className="mb-4">📊 Wynik quizu</h2>
                        <div className="display-1 mb-3">
                            {passed ? '🎉' : '😅'}
                        </div>
                        <h3>{result.score} / {result.total_possible} punktów</h3>
                        <h4 className={passed ? 'text-success' : 'text-danger'}>
                            {result.percentage}% {passed ? '✅ Zaliczony' : '❌ Niezaliczony'}
                        </h4>
                        <Badge bg={passed ? 'success' : 'warning'}>
                            Próg zaliczenia: {quiz.passing_score}%
                        </Badge>
                        <div className="mt-4">
                            <Button variant="primary" onClick={() => navigate(`/courses/${courseId}/lessons/${lessonId}`)}>
                                ← Wróć do lekcji
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    return (
        <Container className="mt-4 mb-5">
            <Button variant="link" onClick={() => navigate(`/courses/${courseId}/lessons/${lessonId}`)}>
                ← Wróć do lekcji
            </Button>

            <Card className="shadow-sm mt-3">
                <Card.Body>
                    <h2>{quiz.title}</h2>
                    <p className="text-muted">{quiz.description}</p>
                    <div className="d-flex gap-3 mb-3">
                        <Badge bg="info">Pytania: {quiz.total_questions}</Badge>
                        {quiz.time_limit_minutes && (
                            <Badge bg="warning">⏱️ {quiz.time_limit_minutes} min</Badge>
                        )}
                        <Badge bg="secondary">Próg: {quiz.passing_score}%</Badge>
                    </div>
                </Card.Body>
            </Card>

            {quiz.questions?.map((question, index) => (
                <Card key={question.id} className="shadow-sm mt-3">
                    <Card.Body>
                        <h5>
                            Pytanie {index + 1}. {question.text}
                            <Badge bg="secondary" className="ms-2">{question.points} pkt</Badge>
                        </h5>

                        {question.question_type === 'text' ? (
                            <Form.Group>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={textAnswers[question.id] || ''}
                                    onChange={(e) => handleTextAnswerChange(question.id, e.target.value)}
                                    placeholder="Wpisz swoją odpowiedź..."
                                />
                            </Form.Group>
                        ) : (
                            question.answers?.map(answer => (
                                <Form.Check
                                    key={answer.id}
                                    type={question.question_type === 'single' ? 'radio' : 'checkbox'}
                                    name={`question_${question.id}`}
                                    label={answer.text}
                                    checked={(answers[question.id] || []).includes(answer.id)}
                                    onChange={() => handleAnswerChange(question.id, answer.id)}
                                />
                            ))
                        )}
                    </Card.Body>
                </Card>
            ))}

            {!attempted && (
                <div className="text-center mt-4">
                    <Button
                        variant="success"
                        size="lg"
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting ? 'Sprawdzanie...' : '📝 Wyślij odpowiedzi'}
                    </Button>
                </div>
            )}
        </Container>
    );
};

export default QuizView;